import type { AccountBase } from '@/models/AccountViewModel';
import {
  userTagCanDelete,
  userTagCanEdit,
  type UserTag,
} from '@/store/usersApi';
import type { ViewingTagSelection } from '@/utils/viewingTagSelection';

/**
 * Investor linked to an account (from `GET .../Accounts/` when the API includes `Investors`).
 */
export interface AccountInvestorRef {
  InvestorId: number;
  InvestorName?: string;
  Name?: string;
}

export type AccountWithInvestors = AccountBase & {
  Investors?: AccountInvestorRef[] | undefined;
};

/** Option for the global "Viewing accounts for" Autocomplete (`tagId` from GET /api/user/tags; null = all accounts). */
export interface ViewingInvestorOption {
  tagId: number | null;
  label: string;
}

export type ViewingTagOptionKind = 'all' | 'firm' | 'user' | 'create';

export interface ViewingTagOption {
  kind: ViewingTagOptionKind;
  label: string;
  selection: ViewingTagSelection | undefined;
  group: '' | 'firm' | 'user';
}

function isUsableViewingTag(tag: UserTag): boolean {
  return Number.isFinite(tag.tagId) && tag.name.trim().length > 0;
}

/** Personal ("My tags") when rights grant Edit or Delete; otherwise firm. */
function isPersonalViewingTag(tag: UserTag): boolean {
  return userTagCanEdit(tag) || userTagCanDelete(tag);
}

function selectionForUsableTag(
  tag: UserTag,
): Exclude<ViewingTagSelection, null> {
  return {
    source: isPersonalViewingTag(tag) ? 'user' : 'firm',
    tagId: tag.tagId,
  };
}

/**
 * Selection `buildViewingTagOptions` would render for this id.
 * Null when the tag is missing or would not be shown.
 */
export function viewingTagSelectionForTagId(
  tagId: number,
  userTags: UserTag[] | undefined,
): ViewingTagSelection {
  const tag = (userTags ?? []).find(
    (candidate) => candidate.tagId === tagId && isUsableViewingTag(candidate),
  );
  if (!tag) {
    return null;
  }
  return selectionForUsableTag(tag);
}

/**
 * Splits a single `/api/user/tags` list into firm ("My firm", read-only) and
 * personal ("My tags", editable) dropdown options. A tag is personal iff its
 * `rights[]` grant Edit or Delete; every other tag is a firm tag.
 */
export function buildViewingTagOptions(params: {
  allAccountsLabel: string;
  createTagLabel: string;
  userTags: UserTag[] | undefined;
}): ViewingTagOption[] {
  const { allAccountsLabel, createTagLabel, userTags } = params;

  const usableTags = (userTags ?? []).filter(isUsableViewingTag);

  const firmOptions = usableTags
    .filter((tag) => !isPersonalViewingTag(tag))
    .map<ViewingTagOption>((tag) => ({
      kind: 'firm',
      label: tag.name.trim(),
      selection: selectionForUsableTag(tag),
      group: 'firm',
    }))
    .sort((left, right) => left.label.localeCompare(right.label));

  const userOptions = usableTags
    .filter((tag) => isPersonalViewingTag(tag))
    .map<ViewingTagOption>((tag) => ({
      kind: 'user',
      label: tag.name.trim(),
      selection: selectionForUsableTag(tag),
      group: 'user',
    }))
    .sort((left, right) => left.label.localeCompare(right.label));

  return [
    {
      kind: 'all',
      label: allAccountsLabel,
      selection: null,
      group: '',
    },
    ...firmOptions,
    ...userOptions,
    {
      kind: 'create',
      label: createTagLabel,
      selection: undefined,
      group: 'user',
    },
  ];
}

/**
 * True when `selection` is All accounts, or a firm/personal option
 * `buildViewingTagOptions` would render for this tag list.
 * A firm id left in session storage by another user is not listed.
 */
export function viewingTagSelectionIsListed(
  selection: ViewingTagSelection,
  userTags: UserTag[] | undefined,
): boolean {
  if (selection === null) {
    return true;
  }
  const listed = viewingTagSelectionForTagId(selection.tagId, userTags);
  return listed !== null && listed.source === selection.source;
}

/**
 * Builds dropdown options from GET /api/user/tags (`tagId` / `name`).
 */
export function buildViewingInvestorOptionsFromUserTags(params: {
  allAccountsLabel: string;
  userTags: UserTag[] | undefined;
}): ViewingInvestorOption[] {
  const { allAccountsLabel, userTags } = params;
  const base: ViewingInvestorOption[] = [
    { tagId: null, label: allAccountsLabel },
  ];
  if (!userTags?.length) {
    return base;
  }
  const rest = userTags
    .filter((t) => Number.isFinite(t.tagId) && t.name.trim().length > 0)
    .map((t) => ({ tagId: t.tagId, label: t.name.trim() }))
    .sort((a, b) => a.label.localeCompare(b.label));
  return [...base, ...rest];
}
